/**
 * useOfflineSync Hook
 * 
 * Gestisce il salvataggio delle azioni offline in localStorage
 * e la sincronizzazione automatica quando la connessione torna online.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const OFFLINE_QUEUE_KEY = 'curacheck_offline_queue';
const SYNC_INTERVAL = 5000; // Check ogni 5 secondi

export const useOfflineSync = (apiUrl) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncIntervalRef = useRef(null);

  // Carica le azioni pending da localStorage all'avvio
  useEffect(() => {
    const savedQueue = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (savedQueue) {
      try {
        const queue = JSON.parse(savedQueue);
        setPendingActions(queue);
        console.log('[OfflineSync] Caricate', queue.length, 'azioni pending da localStorage');
      } catch (e) {
        console.error('[OfflineSync] Errore parsing queue:', e);
      }
    }
  }, []);

  // Salva le azioni pending in localStorage quando cambiano
  useEffect(() => {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(pendingActions));
  }, [pendingActions]);

  // Listener per lo stato della connessione
  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineSync] Connessione ripristinata!');
      setIsOnline(true);
      toast.success('Connessione ripristinata!', {
        description: 'Sincronizzazione in corso...'
      });
    };

    const handleOffline = () => {
      console.log('[OfflineSync] Connessione persa');
      setIsOnline(false);
      toast.warning('Modalità Offline', {
        description: 'Le azioni verranno salvate e inviate quando tornerai online.'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Funzione per sincronizzare le azioni pending
  const syncPendingActions = useCallback(async () => {
    if (!isOnline || pendingActions.length === 0 || isSyncing) {
      return;
    }

    setIsSyncing(true);
    console.log('[OfflineSync] Inizio sincronizzazione di', pendingActions.length, 'azioni...');

    const actionsToSync = [...pendingActions];
    const failedActions = [];
    let successCount = 0;

    for (const action of actionsToSync) {
      try {
        const response = await fetch(`${apiUrl}${action.endpoint}`, {
          method: action.method || 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...action.data,
            // Aggiungi flag per indicare che è una sync offline
            _offline_sync: true,
            _original_timestamp: action.timestamp
          }),
        });

        if (response.ok) {
          successCount++;
          console.log('[OfflineSync] Azione sincronizzata:', action.type);
        } else {
          // Se fallisce per errore server, riprova dopo
          console.error('[OfflineSync] Errore server per azione:', action.type);
          failedActions.push(action);
        }
      } catch (error) {
        // Se fallisce per errore di rete, mantieni in coda
        console.error('[OfflineSync] Errore rete:', error);
        failedActions.push(action);
      }
    }

    // Aggiorna la coda con solo le azioni fallite
    setPendingActions(failedActions);

    if (successCount > 0) {
      toast.success(`${successCount} azioni sincronizzate!`);
    }

    if (failedActions.length > 0) {
      console.log('[OfflineSync] Rimangono', failedActions.length, 'azioni da sincronizzare');
    }

    setIsSyncing(false);
  }, [apiUrl, isOnline, pendingActions, isSyncing]);

  // Sincronizza automaticamente quando torna online
  useEffect(() => {
    if (isOnline && pendingActions.length > 0) {
      // Aspetta un attimo per assicurarsi che la connessione sia stabile
      const timeout = setTimeout(() => {
        syncPendingActions();
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isOnline, pendingActions.length, syncPendingActions]);

  // Polling periodico per sincronizzazione (backup)
  useEffect(() => {
    if (isOnline && pendingActions.length > 0) {
      syncIntervalRef.current = setInterval(() => {
        if (!isSyncing) {
          syncPendingActions();
        }
      }, SYNC_INTERVAL);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isOnline, pendingActions.length, isSyncing, syncPendingActions]);

  /**
   * Esegue un'azione - se online la invia subito, se offline la salva
   * @param {Object} actionConfig - Configurazione dell'azione
   * @param {string} actionConfig.type - Tipo di azione (es. 'meal_confirmed', 'medicine_taken')
   * @param {string} actionConfig.endpoint - Endpoint API
   * @param {Object} actionConfig.data - Dati da inviare
   * @param {Function} actionConfig.onSuccess - Callback se successo (opzionale)
   * @param {Function} actionConfig.onError - Callback se errore (opzionale)
   */
  const executeAction = useCallback(async (actionConfig) => {
    const { type, endpoint, data, method = 'POST', onSuccess, onError } = actionConfig;

    // Aggiungi timestamp per tracking
    const actionData = {
      ...data,
      timestamp: new Date().toISOString()
    };

    if (isOnline) {
      // Online: prova a inviare subito
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(actionData),
        });

        if (response.ok) {
          const result = await response.json();
          onSuccess?.(result);
          return { success: true, data: result };
        } else {
          const error = await response.json();
          onError?.(error);
          return { success: false, error };
        }
      } catch (error) {
        console.error('[OfflineSync] Errore durante invio, salvo offline:', error);
        // Fallback: salva offline
        const offlineAction = {
          id: Date.now().toString(),
          type,
          endpoint,
          method,
          data: actionData,
          timestamp: actionData.timestamp
        };
        setPendingActions(prev => [...prev, offlineAction]);
        toast.info('Salvato offline', {
          description: 'Verrà sincronizzato quando la connessione sarà disponibile'
        });
        return { success: true, offline: true };
      }
    } else {
      // Offline: salva in localStorage
      const offlineAction = {
        id: Date.now().toString(),
        type,
        endpoint,
        method,
        data: actionData,
        timestamp: actionData.timestamp
      };
      
      setPendingActions(prev => [...prev, offlineAction]);
      
      console.log('[OfflineSync] Azione salvata offline:', type);
      toast.info('Salvato offline', {
        description: 'Verrà sincronizzato quando tornerai online'
      });
      
      // Chiama onSuccess anche per le azioni offline (UX fluida)
      onSuccess?.({ offline: true });
      
      return { success: true, offline: true };
    }
  }, [apiUrl, isOnline]);

  /**
   * Forza la sincronizzazione manuale
   */
  const forcSync = useCallback(() => {
    if (isOnline) {
      syncPendingActions();
    } else {
      toast.error('Impossibile sincronizzare', {
        description: 'Nessuna connessione internet'
      });
    }
  }, [isOnline, syncPendingActions]);

  /**
   * Cancella tutte le azioni pending (per debug/reset)
   */
  const clearPendingActions = useCallback(() => {
    setPendingActions([]);
    localStorage.removeItem(OFFLINE_QUEUE_KEY);
    console.log('[OfflineSync] Coda svuotata');
  }, []);

  return {
    isOnline,
    pendingActions,
    pendingCount: pendingActions.length,
    isSyncing,
    executeAction,
    forcSync,
    clearPendingActions
  };
};

export default useOfflineSync;
